import {
	ApiError,
	AppError,
	AppMimeType,
	ErrorCode,
	Language,
} from '@/types/types';
import { formatDistance, Locale } from 'date-fns';
import { isAxiosError } from 'axios';
import { MIME_TYPES } from '@mantine/dropzone';
import { OrderStatus, TransportType } from '@/api/OrderApi';
import { Product } from '@/api/ProductApi';
import APP_CONFIG from '@/configs/appConfig';
import moment from 'moment';

export function formatDateForPresentation(date: moment.MomentInput) {
	if (!date) return '-';
	return moment(date).format('DD.MM.YYYY');
}

export function formatTimeForPresentation(
	date: moment.MomentInput | string = new Date()
) {
	return moment(date).format('HH:mm');
}

export function formatExactDate(date: moment.MomentInput = new Date()) {
	return `${formatDateForPresentation(date)}, ${formatTimeForPresentation(
		date
	)}`;
}

const PASSWORD_REGEX = /^(?=\D*\d)[^ ]{6,}$/;

/**
 *
 * @param password one number and at least 8
 * @returns
 */
export function validatePassword(password: string) {
	return PASSWORD_REGEX.test(password);
}

export function sortByDate(
	dateA: string | Date,
	dateB: string | Date,
	order: 'asc' | 'desc' = 'asc'
) {
	const first = moment(dateA).valueOf(),
		second = moment(dateB).valueOf();

	return order === 'asc' ? first - second : second - first;
}

export function stripHTML(string: string) {
	return string.replace(/(<([^>]+)>)/gi, '');
}

export function handleUnload(e: BeforeUnloadEvent) {
	e.preventDefault();
}

export function formatDateFromNow(date: Date, locale: Locale) {
	return formatDistance(date, new Date(), {
		locale,
		includeSeconds: true,
		addSuffix: true,
	});
}

export type FormatPriceOptions = Intl.NumberFormatOptions & {
	useZlotySymbol?: boolean;
};

export const formatPrice = (value = 0, opts?: FormatPriceOptions) => {
	const { useZlotySymbol, ...intlOpts } = opts ?? {};

	const formatter = new Intl.NumberFormat('pl-PL', {
		style: 'currency',
		currency: 'PLN',
		currencyDisplay: useZlotySymbol ? 'narrowSymbol' : 'code',
		maximumFractionDigits: !value ? 0 : undefined,
		...intlOpts,
	});

	return formatter.format(value);
};

export function checkIfScreenZoomedIn() {
	return window.devicePixelRatio >= 1.2;
}

export const isAppError = (error: unknown): error is AppError => {
	return !!(error as AppError).status && !!(error as AppError).stack;
};

export const getErrorCodes = (error: unknown) => {
	if (isAxiosError<ApiError>(error) && error.response)
		return Array.isArray(error.response.data.message)
			? error.response.data.message
			: [error.response.data.message];

	return [ErrorCode.GENERIC];
};

export const errorCodesToMessage = (errorCodes: ErrorCode[]) =>
	errorCodes.map((code) => `errors.${code}`).join(', ');

export function getFileSrc(path?: string) {
	return `${import.meta.env.VITE_API_URL_UPLOADS}${path}`;
}

export function isDefaultLanguage(language: Language): language is 'PL' {
	return language === APP_CONFIG.DEFAULT_LANGUAGE;
}

export function formatFullName(firstName?: string, lastName?: string) {
	if (firstName && lastName) return `${firstName} ${lastName}`;
	if (!firstName && !lastName) return `-`;
	return firstName || lastName;
}

export function getMimeTypeName(type: AppMimeType) {
	switch (type) {
		case MIME_TYPES.pdf:
			return 'PDF';
		case MIME_TYPES.zip:
			return 'ZIP';

		default:
			return 'Nieznany typ pliku';
	}
}

export function formatFileSize(bytes: number) {
	if (bytes < 1024) return `${bytes} B`;
	if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
	return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function getFileTypeLabel(mimetype: string) {
	const known = getMimeTypeName(mimetype as AppMimeType);
	if (known !== 'Nieznany typ pliku') return known;

	const subtype = mimetype.split('/')[1];
	return subtype ? subtype.toUpperCase() : mimetype;
}

export function getOrderStatusName(status: OrderStatus) {
	switch (status) {
		case OrderStatus.NOT_SENT:
			return 'Nie wysłane';
		case OrderStatus.SENT:
			return 'Wysłano zamówienie';
		case OrderStatus.CONFIRMED:
			return 'Potwierdzone';
		case OrderStatus.COMPLETED:
			return 'Zakończone';
	}
}

export function getOrderStatusBadgeData(status: OrderStatus) {
	switch (status) {
		case OrderStatus.NOT_SENT:
			return {
				name: getOrderStatusName(status),
				color: 'yellow',
			};
		case OrderStatus.SENT:
			return {
				name: getOrderStatusName(status),
				color: 'green',
			};
		case OrderStatus.CONFIRMED:
			return {
				name: getOrderStatusName(status),
				color: 'blue',
			};
		case OrderStatus.COMPLETED:
			return {
				name: getOrderStatusName(status),
				color: 'orange',
			};
	}
}

export function removeWhiteSpaces(value: string) {
	return value.replace(/\s/g, '');
}

export function fileTypeToHeader(fileType: 'csv' | 'xlsx' | 'pdf') {
	switch (fileType) {
		case 'xlsx':
			return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
		case 'csv':
			return 'text/csv';
		case 'pdf':
			return 'application/pdf';
		default:
			return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
	}
}

export function getMinShippingDate(products: Product[]) {
	const minLeadTimeDays = products.reduce(
		(min, product) => Math.max(min, product.productType?.minLeadTimeDays || 0),
		0
	);

	return moment().add(minLeadTimeDays, 'days').toDate();
}

/** First date on or after `date` that is not Saturday (6) or Sunday (0). */
export function getFirstWeekdayOnOrAfter(date: Date) {
	const result = new Date(date);
	while ([0, 6].includes(result.getDay())) result.setDate(result.getDate() + 1);
	return result;
}

export function mapTransportTypeName(transportType: TransportType) {
	switch (transportType) {
		case TransportType.EWAN:
			return 'Ewan';
		case TransportType.COURIER:
			return 'Kurier';
		case TransportType.SELF_PICKUP:
			return 'Odbiór osobisty';
	}
}
